import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/system";
import { BackwardBack, ChangeRefresh, Search } from "@pmag/icons";
import { Typography, useSessionStorage } from "@pmag/ui-components";
import { useRestart } from "@src/main/useRestart";
import { UsageList } from "@src/pages/compatibilityCheck/components/usageList/UsageList";
import {
    defaultSessionFilterState,
    sessionFilterSchema,
    type SessionFilterState,
    type ToolbarFormState,
} from "@src/pages/compatibilityCheck/components/usageList/UsageListFormSchema";
import { categoryTabSchema } from "@src/pages/componentGroupOverview/ComponentGroupOverviewPage";
import { ComponentGroupOverviewCategoryTabs } from "@src/pages/componentGroupOverview/ComponentGroupOverviewPage.utils";
import * as React from "react";
import { FormattedMessage } from "react-intl";

import {
    ButtonContainer,
    InputWithButtonsContainer,
    NavigationButton,
    OrContainer,
    ResetButton,
    Root,
    SingleTextInputContainer,
    StyledButton,
    StyledTextInput,
    TextInputContainer,
    TopContainer,
} from "./CompatibilityCheckPage.styles";

export const dataTestId = {
    container: "sparePartsFinder.compatibilityCheckPage.container",
    searchContainer: "sparePartsFinder.compatibilityCheckPage.searchContainer",
    restartButton: "sparePartsFinder.compatibilityCheckPage.button.restart",
    submitButton: "sparePartsFinder.compatibilityCheckPage.button.submit",
    resetButton: "sparePartsFinder.compatibilityCheckPage.button.reset",
};

export const CompatibilityCheckPage: React.FunctionComponent = () => {
    const theme = useTheme();
    const { restart, showRestartButton } = useRestart();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [appliedFilter, setAppliedFilter] = useSessionStorage<SessionFilterState>("filter", defaultSessionFilterState, sessionFilterSchema);

    const [articleNumber, setArticleNumber] = React.useState<string>("");
    const [articleName, setArticleName] = React.useState<string>("");

    React.useEffect(() => {
        if (appliedFilter.articleNumber && appliedFilter.articleNumber != articleNumber) setArticleNumber(appliedFilter.articleNumber);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilter.articleNumber]);

    React.useEffect(() => {
        if (appliedFilter.articleDescription && appliedFilter.articleDescription != articleName) setArticleName(appliedFilter.articleDescription);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilter.articleDescription]);

    // reset component group tab
    const [categoryTab, setCategoryTab] = useSessionStorage<ComponentGroupOverviewCategoryTabs>(
        "sparePartsFinderOverviewCategoryTab",
        ComponentGroupOverviewCategoryTabs.Frame,
        categoryTabSchema,
    );
    if (categoryTab !== ComponentGroupOverviewCategoryTabs.Frame) setCategoryTab(ComponentGroupOverviewCategoryTabs.Frame);

    const onToolbarFilterChange = (state: ToolbarFormState) => {
        setAppliedFilter({
            ...appliedFilter,
            ...state,
            componentGroup: state?.componentGroup?.value?.toString() || null,
        });
    };

    const onSubmit = () => {
        setAppliedFilter({
            articleDescription: articleName.length > 0 ? articleName : undefined,
            articleNumber: articleNumber.length > 0 ? articleNumber : undefined,
        });
    };

    const onReset = () => {
        setArticleNumber("");
        setArticleName("");
        setAppliedFilter({ articleNumber: undefined, articleDescription: undefined });
    };

    const onKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === "Enter") onSubmit();
    };

    return (
        <Root data-testid={dataTestId.container} pb={8}>
            <TopContainer>
                {!isSmallScreen && (
                    <Typography variant="heading-07">
                        <FormattedMessage id="sparePartsFinder.compatibilityCheckPage.title" defaultMessage="Compatibility check" />
                    </Typography>
                )}

                <NavigationButton
                    variant="text"
                    startIcon={<BackwardBack size="small" />}
                    data-testid={dataTestId.restartButton}
                    onClick={restart}
                    hidden={!showRestartButton}
                >
                    <Typography variant="body-short-01">
                        <FormattedMessage id="sparePartsFinder.common.button.restart" defaultMessage="Restart" />
                    </Typography>
                </NavigationButton>
            </TopContainer>

            {isSmallScreen && (
                <Box mb={6}>
                    <Typography variant="heading-07">
                        <FormattedMessage id="sparePartsFinder.compatibilityCheckPage.title" defaultMessage="Compatibility check" />
                    </Typography>
                </Box>
            )}

            <Box mb={6}>
                <Typography variant="body-short-01">
                    <FormattedMessage
                        id="sparePartsFinder.compatibilityCheckPage.description"
                        defaultMessage="Please enter the article number or the article name to see which vehicles your spare part is compatible with."
                    />
                </Typography>
            </Box>

            <InputWithButtonsContainer data-testid={dataTestId.searchContainer}>
                <TextInputContainer>
                    <SingleTextInputContainer>
                        <Typography variant="label-02">
                            <FormattedMessage id="sparePartsFinder.compatibilityCheckPage.articleNumberInputTitle" defaultMessage="Article Number" />
                        </Typography>

                        <StyledTextInput
                            value={articleNumber}
                            onChange={(ev) => setArticleNumber(ev.currentTarget.value)}
                            disabled={articleName.length > 0}
                            onKeyPress={onKeyPress}
                        />
                    </SingleTextInputContainer>

                    <OrContainer>
                        <Typography variant="label-02">
                            <FormattedMessage id="sparePartsFinder.compatibilityCheckPage.or" defaultMessage="or" />
                        </Typography>
                    </OrContainer>

                    <SingleTextInputContainer>
                        <Typography variant="label-02">
                            <FormattedMessage id="sparePartsFinder.compatibilityCheckPage.articleNameInputTitle" defaultMessage="Article Name" />
                        </Typography>

                        <StyledTextInput
                            value={articleName}
                            onChange={(ev) => setArticleName(ev.currentTarget.value)}
                            disabled={articleNumber.length > 0}
                            onKeyPress={onKeyPress}
                        />
                    </SingleTextInputContainer>
                </TextInputContainer>

                <ButtonContainer>
                    <ResetButton variant="text" onClick={onReset} startIcon={<ChangeRefresh size="small" />} data-testid={dataTestId.resetButton}>
                        <FormattedMessage id="sparePartsFinder.compatibilityCheckPage.resetButtonTitle" defaultMessage="Reset all fields" />
                    </ResetButton>

                    <StyledButton variant="primary" onClick={onSubmit} icon={<Search size="small" />} data-testid={dataTestId.submitButton}>
                        <FormattedMessage id="sparePartsFinder.compatibilityCheckPage.submitButtonTitle" defaultMessage="Search" />
                    </StyledButton>
                </ButtonContainer>
            </InputWithButtonsContainer>

            <UsageList appliedFilter={appliedFilter} onFilterChange={onToolbarFilterChange} />
        </Root>
    );
};
